import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Shield, LogOut, Trash2, ChevronRight, Lightbulb, Loader2, X, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, API } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { pushSupported, isSubscribed, enablePush, disablePush } from "@/lib/push";

function NotificationsRow() {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (supported) isSubscribed().then(setSubscribed).catch(() => {});
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
        toast("Notificações desativadas.");
      } else {
        await enablePush();
        setSubscribed(true);
        toast.success("Notificações ativadas! Você será avisada dos novos stickers ✨");
      }
    } catch (e) {
      toast.error(e.message || "Não foi possível alterar as notificações.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid="account-notifications"
      onClick={toggle}
      disabled={busy || !supported}
      className="w-full flex items-center gap-3 px-5 py-4 transition-colors hover:bg-[#FCFBF9] disabled:opacity-60"
    >
      {subscribed ? <Bell size={19} className="text-[#C58B90]" /> : <BellOff size={19} className="text-[#C58B90]" />}
      <span className="flex-1 text-left text-sm font-medium">
        {supported ? (subscribed ? "Notificações ativadas" : "Ativar notificações") : "Notificações não suportadas"}
      </span>
      {busy ? (
        <Loader2 size={17} className="animate-spin text-[#C58B90]" />
      ) : (
        supported && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${subscribed ? "bg-[#EAF3EB] text-[#5FA36A]" : "bg-[#F2E6E7] text-[#C58B90]"}`}>
            {subscribed ? "ON" : "OFF"}
          </span>
        )
      )}
    </button>
  );
}

function Row({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-5 py-4 transition-colors hover:bg-[#FCFBF9] ${
        danger ? "text-[#C0555A]" : "text-[#2C2A29]"
      }`}
    >
      <Icon size={19} className={danger ? "text-[#C0555A]" : "text-[#C58B90]"} />
      <span className="flex-1 text-left text-sm font-medium">{label}</span>
      <ChevronRight size={17} className="text-[#C7C1BC]" />
    </button>
  );
}

export function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPwd, setShowPwd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const changePwd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(res.data.message);
      setShowPwd(false);
      setCur("");
      setNext("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
    toast("Até logo! 💗");
  };

  const deleteAccount = async () => {
    setLoading(true);
    try {
      await api.delete("/auth/me");
      logout();
      navigate("/login");
      toast("Sua conta foi excluída.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex flex-col items-center text-center py-4">
        <Logo size={64} />
        <h1 className="font-serif text-2xl mt-4">{user?.name}</h1>
        <p className="text-sm text-[#7A7571]">{user?.email}</p>
        {user?.role === "admin" && (
          <span className="mt-2 text-xs font-semibold uppercase tracking-widest text-[#C58B90] bg-[#F2E6E7] px-3 py-1 rounded-full">
            Administradora
          </span>
        )}
      </div>

      {user?.role === "admin" && (
        <div className="bg-white rounded-3xl border border-[#F2E6E7] bc-shadow-soft overflow-hidden">
          <Row icon={Shield} label="Painel Administrativo" testid="account-admin" onClick={() => navigate("/admin")} />
        </div>
      )}

      <div className="bg-white rounded-3xl border border-[#F2E6E7] bc-shadow-soft overflow-hidden divide-y divide-[#F2E6E7]">
        <NotificationsRow />
        <Row icon={Lightbulb} label="Sugira um Sticker" testid="account-suggest" onClick={() => navigate("/sugestoes")} />
        <Row icon={Lock} label="Alterar minha senha" testid="account-change-pwd" onClick={() => setShowPwd(true)} />
        <Row
          icon={Shield}
          label="Política de Privacidade"
          testid="account-privacy"
          onClick={() => window.open(`${API}/privacy`, "_blank")}
        />
        <Row icon={LogOut} label="Sair" testid="account-logout" onClick={doLogout} />
      </div>

      <div className="bg-white rounded-3xl border border-[#F2E6E7] bc-shadow-soft overflow-hidden">
        <Row icon={Trash2} label="Excluir minha conta" testid="account-delete" danger onClick={() => setShowDelete(true)} />
      </div>

      {showPwd && (
        <Modal onClose={() => setShowPwd(false)} title="Alterar senha">
          <form onSubmit={changePwd} className="space-y-4">
            <input
              type="password"
              placeholder="Senha atual"
              data-testid="cur-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-2xl bg-[#FCFBF9] border border-[#E8CFD1] outline-none focus:border-[#C58B90] transition-colors"
            />
            <input
              type="password"
              placeholder="Nova senha (mín. 6 caracteres)"
              data-testid="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-2xl bg-[#FCFBF9] border border-[#E8CFD1] outline-none focus:border-[#C58B90] transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="save-password"
              className="w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70"
            >
              {loading && <Loader2 size={17} className="animate-spin" />}
              Salvar nova senha
            </button>
          </form>
        </Modal>
      )}

      {showDelete && (
        <Modal onClose={() => setShowDelete(false)} title="Excluir conta">
          <p className="text-sm text-[#7A7571] mb-5">
            Tem certeza? Esta ação é permanente e todos os seus favoritos serão removidos. Você perderá o acesso ao clube.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDelete(false)}
              className="flex-1 h-12 rounded-full bg-[#F2E6E7] text-[#2C2A29] font-medium hover:bg-[#E8CFD1] transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={deleteAccount}
              disabled={loading}
              data-testid="confirm-delete"
              className="flex-1 h-12 rounded-full bg-[#C0555A] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A8474C] transition-colors disabled:opacity-70"
            >
              {loading && <Loader2 size={17} className="animate-spin" />}
              Excluir
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-3xl p-6 bc-shadow-hover animate-float-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#7A7571]" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
